const _ = require('lodash');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { MODAL_NAMES } = require('@/vue/modals/MODAL_NAMES');

class DeviceLockService {
    /**
     * @param {LangService} langService
     * @param {ToastFactory} toastFactory
     * @param {UserService} userService
     * @param {VueModalFactory} vueModalFactory
     */
    constructor(
        langService,
        toastFactory,
        userService,
        vueModalFactory
    ) {
        /**
         * @type {LangService}
         */
        this.langService = langService;

        /**
         * @type {ToastFactory}
         */
        this.toastFactory = toastFactory;

        /**
         * @type {UserService}
         */
        this.userService = userService;

        /**
         * @type {VueModalFactory}
         */
        this.vueModalFactory = vueModalFactory;

        /**
         * How long before the device locks should a warning pop up that the device is going to lock?
         *
         * @type {number}
         */
        this.showToastForSeconds = 30;

        /**
         * @private
         * @type {number|null}
         */
        this.autoLockAfterSeconds = null;

        /**
         * @private
         * @type {HTMLElement|null}
         */
        this.autoLockToast = null;

        /**
         * @private
         * @type {null}
         */
        this.inactivityCheckInterval = null;

        /**
         * @private
         * @type {Date}
         */
        this.lastActivityAt = null;

        /**
         * @type {ConsoleLogger}
         */
        this.log = logFactory('DeviceLockService');
    }

    /**
     * This should only be called once, if auto locking is in use on the device.
     */
    enableActivityMonitoring() {
        if (this.lastActivityAt !== null) {
            // Prevent adding the event listeners multiple times.
            return;
        }

        // Set the time of the last activity whenever it happens.
        const onActivity = _.debounce(
            () => {
                this.lastActivityAt = new Date();

                if (this.autoLockToast) {
                    this.toastFactory.clear(this.autoLockToast);
                    this.autoLockToast = null;
                }
            },
            100,
        );
        // window.addEventListener('focus', onActivity, { passive: true });
        window.addEventListener('keydown', onActivity, { passive: true });
        window.addEventListener('mousedown', onActivity, { passive: true });
        window.addEventListener('mousemove', onActivity, { passive: true });
        window.addEventListener('touchstart', onActivity, { passive: true });

        onActivity();
    }

    setAutoLockAfterMinutes(minutes) {
        if (minutes) {
            this.enableActivityMonitoring();
            this.autoLockAfterSeconds = minutes * 60;
            this.startInactivityCheck();
        } else {
            this.autoLockAfterSeconds = null;
            this.stopInactivityCheck();
        }
    }

    startInactivityCheck() {
        this.inactivityCheckInterval = setInterval(() => {
            if (!this.lastActivityAt) {
                return;
            }

            if (this.deviceIsLocked() || !this.userService.isLoggedIn()) {
                // Device is already locked. Don't do anything.
                return;
            }

            const inactiveForSeconds = Math.floor((new Date().getTime() - this.lastActivityAt.getTime()) / 1000);
            const remainingSeconds = this.autoLockAfterSeconds - inactiveForSeconds;

            if (remainingSeconds <= 0) {
                this.log.log('Locking device due to inactivity.');
                this.lockDevice();
            } else if (remainingSeconds <= this.showToastForSeconds) {
                if (this.autoLockToast) {
                    this.toastFactory.updateText(
                        this.autoLockToast,
                        this.langService.getText('DEVICE.INACTIVITY_TOAST_MESSAGE', { seconds: remainingSeconds }),
                    );
                } else {
                    this.autoLockToast = this.toastFactory.warningTop(
                        'DEVICE.INACTIVITY_TOAST_TITLE',
                        this.langService.getText('DEVICE.INACTIVITY_TOAST_MESSAGE', { seconds: remainingSeconds }),
                        {
                            timeOut: 0,
                        },
                    );
                }
            }
        }, 1000);
    }

    stopInactivityCheck() {
        clearInterval(this.inactivityCheckInterval);
    }

    deviceIsLocked() {
        return document.getElementsByClassName('switch-user-modal').length > 0;
    }

    lockDevice() {
        if (this.deviceIsLocked()) {
            return;
        }

        this.log.log('Locking device.');

        // Clear the countdown toast.
        if (this.autoLockToast) {
            this.toastFactory.clear(this.autoLockToast);
            this.autoLockToast = null;
        }

        this.userService.lockCurrentUserSession()
            .then(() => {})
            .catch(() => {});

        this.vueModalFactory.openAndIgnoreResult(MODAL_NAMES.SWITCH_USER);
    }
}

module.exports = DeviceLockService;
